import { useMemo } from 'react';

import { hasTierFeature } from '../../../shared/subscriptions';
import { window } from '../../globals';
import type { FlagName } from '../../../shared/featureFlags/types';

/**
 * Checks if a given flag is present in the window.enabledFeatureFlags,
 * which is hydrated in the template from the backend. Depends on having
 * the window global being set with a user and organization.
 *
 * Due to the use of window, this should only ever be used on the front-end.
 *
 * @param {FlagName} flagName - A feature flag name.
 */
export const isFeatureEnabled = (flagName: FlagName): boolean =>
  // Check this first since it's a Set lookup and faster than the array lookup.
  (
    !!window.user?.organization
    && hasTierFeature(window.user.organization.stripe_plan, flagName, window.user.organization.stripePriceLimit)
  ) ||
  (window?.enabledFeatureFlags ?? []).some(featureFlag => featureFlag.flag === flagName);

/**
 * Hook which memoizes the isFeatureEnabled check across re-renders in components.
 * Changes only when `flagName` or `enabledFeatureFlags` actually change.
 * This provides a minor perf improvement, as we don't need to iterate through enabledFeatureFlags[]
 * on every re-render of the component like with `isFeatureEnabled`.
 *
 * @param {FlagName} flagName - A feature flag name.
 */
export const useIsFeatureEnabled = (flagName: FlagName) => useMemo(() =>
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We need enabledFeatureFlags to be included, in case it is updated.
  isFeatureEnabled(flagName), [window.enabledFeatureFlags, flagName]);

/**
 * Like isFeatureEnabled, but additionally requires the user to be a super admin.
 * Use for "super feature flags" — flags enabled on real organizations for testing
 * by FOSSA staff before being released to that organization's regular users.
 */
export const isSuperFeatureEnabled = (flagName: FlagName): boolean =>
  isFeatureEnabled(flagName) && !!window.user?.super;

/**
 * Hook variant of isSuperFeatureEnabled. Memoized like useIsFeatureEnabled.
 */
export const useIsSuperFeatureEnabled = (flagName: FlagName) => useMemo(() =>
  // eslint-disable-next-line react-hooks/exhaustive-deps -- match existing useIsFeatureEnabled
  isSuperFeatureEnabled(flagName), [window.enabledFeatureFlags, flagName]);
